import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
    DataRange,
    DownloadState,
    EnabledTriggers,
    ExportConfig,
    ExportEmptyOptions,
    ExportNullAsOptions,
    ExportSortOptions,
    FileFormatMapping,
    FileFormats,
    IntegrationsConfig,
    JavaPropertiesEncodingOptions,
    JavaPropertiesSeparatorOptions,
    ModuleProps,
    QaIssues,
    Repositories,
} from '../../types';
import getDirectoryPrefixValue from '../../utils/getDirectoryPrefixValue';
import getExportLanguages from '../../utils/getExportLanguages';
import getZipStructure from '../../utils/getZipStructure';
import updateExportConfigFileStructure from '../../utils/updateExportConfigFileStructure';

const initialState: Partial<DownloadState> = {};

const slice = createSlice({
    name: 'downloadData',
    initialState,
    reducers: {
        populateDownloadData: (
            state,
            action: PayloadAction<{
                moduleProps: ModuleProps;
                fileFormats: FileFormats;
                dataRange: DataRange;
                exportSortOptions: ExportSortOptions;
                exportEmptyOptions: ExportEmptyOptions;
                exportNullAsOptions: ExportNullAsOptions;
                javaPropertiesSeparatorOptions: JavaPropertiesSeparatorOptions;
                javaPropertiesEncodingOptions: JavaPropertiesEncodingOptions;
                enabledTriggers: EnabledTriggers;
                integrationsConfig: IntegrationsConfig;
                repositories: Repositories;
                fileFormatMapping: FileFormatMapping;
                projectBranchName: string | null;
                qaIssues: QaIssues;
            }>,
        ): DownloadState => {
            const {
                moduleProps,
                dataRange,
                fileFormats,
                exportSortOptions,
                exportEmptyOptions,
                exportNullAsOptions,
                javaPropertiesSeparatorOptions,
                javaPropertiesEncodingOptions,
                enabledTriggers,
                integrationsConfig,
                repositories,
                fileFormatMapping,
                projectBranchName,
                qaIssues,
            } = action.payload;

            return {
                ...state,
                ...moduleProps,
                exportConfig: {
                    ...moduleProps.exportConfig,
                    exportLangs: getExportLanguages(moduleProps?.project?.langs, moduleProps.exportConfig?.exportLangs),
                    zipStructure: getZipStructure(moduleProps?.exportConfig.fileFormat, moduleProps?.exportConfig),
                    directoryPrefix: getDirectoryPrefixValue(
                        moduleProps?.exportConfig.fileFormat,
                        moduleProps?.exportConfig,
                    ),
                    // The second condition is to fix current customers' projects. All new projects are fixed.
                    // TODO Should be removed after couple of weeks.
                    filenamesOriginal:
                        moduleProps?.exportConfig.filenamesOriginal ||
                        (!moduleProps?.exportConfig.filenamesOriginal && !moduleProps?.exportConfig.filenamesCustom),
                },
                dataRange,
                fileFormats,
                exportSortOptions,
                exportEmptyOptions,
                exportNullAsOptions,
                javaPropertiesSeparatorOptions,
                javaPropertiesEncodingOptions,
                enabledTriggers,
                integrationsConfig,
                repositories,
                fileFormatMapping,
                projectBranchName,
                qaIssues,
            };
        },
        updateExportConfig: (state, action: PayloadAction<ExportConfig>): Partial<DownloadState> => {
            const exportConfig = action.payload;
            const exportConfigUpdated = updateExportConfigFileStructure(exportConfig.fileFormat, exportConfig);

            return {
                ...state,
                exportConfig: {
                    ...exportConfig,
                    ...exportConfigUpdated,
                },
            };
        },
    },
});

export default slice.reducer;

export const downloadDataActions = slice.actions;
